import { useDisclosure } from '@chakra-ui/react';
import { isAfter, isBefore } from 'date-fns';
import React from 'react';

import { ListUtils } from '../../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import StyledList from '../../../../components/StyledList';
import { pageKeywords, pagePaths } from '../../../../constants/Page';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import { DateChangerState } from '../../../../hooks/DateChangers';
import { useEngeneNavigate } from '../../../../hooks/navigate';
import { AssignedDailyMatterSummary } from '../../../../models/gen/assignedDailyMatterSummary';
import { Formatter } from '../../../../utils/FormatUtils';
import { useMasterContext } from '../../../MasterProvider';
import { MatterDetailModal } from '../../../modals/MatterDetail';
import { DailyOperationState } from '../DailyOperation';
import { ShiftCalendarItemAssigned, ShiftCalenderItemUnassigned } from './item';

/**
 *
 */
type DailyMatterListProps = DailyOperationState & {
  dateState: DateChangerState;
  /**
   * 表示開始日
   */
  startDate: Date;
  /**
   * 表示終了日
   */
  endDate: Date;
};

/**
 * 日毎現場リスト
 * @returns
 */
const DailyMatterList: React.FunctionComponent<DailyMatterListProps> = (
  props
) => {
  const { actions, state } = useGlobalStateMachine();
  const navigate = useEngeneNavigate();
  const master = useMasterContext();
  const holiday = React.useMemo<number>(
    () =>
      state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_HOLIDAY as number,
    [state.appConfig.facilitySettings]
  );
  const leave = React.useMemo<number>(
    () =>
      state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_WITH_PAY as number,
    [state.appConfig.facilitySettings]
  );

  const [selectedMatterId, setSelectedMatterId] = React.useState<number>();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const setDateState = React.useCallback(
    (state: DateChangerState) =>
      actions.updatePageInfo({
        DAILY_OPERATION: { dateState: state },
      }),
    []
  );

  const onClickOperation = (assignMessageId: number) => {
    setDateState(props.dateState);
    navigate(`${pagePaths.ASSIGNMENT_SE}/${assignMessageId}`);
  };
  const onClickWorkerAssign = (dailyMatterId: number) => {
    setDateState(props.dateState);
    navigate(`${pagePaths.WORKER_ASSIGNMENT}/${dailyMatterId}`);
  };
  const onClickMatterExpense = (dailyMatterId?: number, date?: string) => {
    setDateState(props.dateState);
    if (TypeUtils.isNilNumber(dailyMatterId)) {
      if (TypeUtils.isNilString(date)) {
        navigate(pagePaths.EXPENSE_REIMBURSEMENT);
      } else {
        const dateObj = Formatter.createDate(date);
        navigate(
          `${
            pagePaths.EXPENSE_REIMBURSEMENT
          }?year=${dateObj.getFullYear()}&month=${
            dateObj.getMonth() + 1
          }&half=${
            dateObj.getDate() <= (master.applyUnit.expense1?.endDay || 15)
              ? 1
              : 2
          }`
        );
      }
    } else {
      navigate(
        `${pagePaths.MATTER_EXPENSE}/${dailyMatterId}${
          TypeUtils.isNilString(date) ? '' : `?date=${date}`
        }`
      );
    }
  };

  const items: Array<React.ReactNode> = [];
  (props.results || [])
    .filter(({ date: dateString }) => {
      const date = Formatter.createDate(dateString);
      const endDate = Formatter.createDate(props.endDate.toDateString());
      endDate.setDate(endDate.getDate()); // + 1
      return !(isBefore(date, props.startDate) || isAfter(date, endDate));
    })
    .forEach(
      ({
        date: dateString,
        workApplyId,
        AssignedDailyMatterSummaries: summary,
        attendanceTypeId,
      }) => {
        const workApply = props.reference?.WorkApplies?.find(
          (wa) => wa.workApplyId === workApplyId
        );
        if (!TypeUtils.isNil(summary) && summary.length > 0) {
          summary
            .sort((a, b) =>
              a.workDateFrom !== b.workDateFrom
                ? ListUtils.sort<AssignedDailyMatterSummary>(
                    (result) => result.workDateFrom
                  )(a, b)
                : a.requestTime !== b.requestTime
                ? ListUtils.sort<AssignedDailyMatterSummary>(
                    (result) => result.requestTime
                  )(a, b)
                : ListUtils.sort<AssignedDailyMatterSummary>(
                    (result) => result.assignWorkerId
                  )(a, b)
            )
            .forEach((item, _, array) =>
              items.push(
                <ShiftCalendarItemAssigned
                  dailyMatterSummary={item}
                  dateString={dateString}
                  isApplied={
                    TypeUtils.isNonNilNumber(workApplyId) &&
                    TypeUtils.isNonNilString(workApply?.applyTime)
                  }
                  isOutsourcer={!!state.userInfo?.roleOptions?.isOutsourcer}
                  onClickWorkerAssignment={
                    !TypeUtils.isNil(master.role.representative) &&
                    state.userInfo?.role_id.includes(
                      master.role.representative?.id
                    ) &&
                    item.assignStatus !== 0
                      ? () => onClickWorkerAssign(item.dailyMatterId!)
                      : undefined
                  }
                  onClickAssignment={() =>
                    item.assignStatus !== 0
                      ? onClickOperation(item.assignMessageId!)
                      : undefined
                  }
                  onClickMatterName={() => {
                    setSelectedMatterId(item.matterId);
                    onOpen();
                  }}
                  onClickWorkReport={
                    item.assignStatus === 0 || !item.isLeader
                      ? undefined
                      : () => {
                          setDateState(props.dateState);
                          navigate(
                            `${pagePaths.WORK_REPORT}/${item.dailyMatterId}/${pageKeywords.mode.EDIT}`
                          );
                        }
                  }
                  onClickMatterExpense={() =>
                    onClickMatterExpense(item.dailyMatterId, dateString)
                  }
                  onClickWorkContent={
                    item.assignStatus === 0
                      ? undefined
                      : () => {
                          const index = array
                            .filter(
                              (e) => e.userCodeWorker === item.userCodeWorker
                            )
                            .map((e) => e.assignWorkerId)
                            .indexOf(item.assignWorkerId!);
                          setDateState(props.dateState);
                          navigate(
                            `${pagePaths.WORK_CONTENT}/${dateString.replaceAll(
                              '/',
                              '-'
                            )}/${item.userCodeWorker!}/${
                              // !!item.isApproved
                              TypeUtils.isNonNilNumber(workApplyId) &&
                              TypeUtils.isNonNilString(workApply?.applyTime) // 勤怠申請の有無のみで判定(workApplyId の有無)
                                ? pageKeywords.mode.VIEW
                                : pageKeywords.mode.EDIT
                            }${
                              TypeUtils.isNilString(
                                state.userInfo?.outsourcer_code
                              )
                                ? `?index=${index}`
                                : `?outsourcerCode=${state.userInfo?.outsourcer_code}&index=${index}`
                            }`
                          );
                        }
                  }
                  outsourcerMembers={props.outsourcerMemberSummary}
                />
              )
            );
        } else if (TypeUtils.isNilNumber(attendanceTypeId)) {
          // 実績も予定もない
          items.push(
            <ShiftCalenderItemUnassigned
              attendanceTypeId={attendanceTypeId}
              dateString={dateString}
              isApplied={
                TypeUtils.isNonNilNumber(workApplyId) &&
                TypeUtils.isNonNilString(workApply?.applyTime)
              }
              isOutsourcer={!!state.userInfo?.roleOptions?.isOutsourcer}
              onClickMatterExpense={
                state.userInfo?.roleOptions.isOutsourcer
                  ? undefined
                  : () => onClickMatterExpense(undefined, dateString)
              }
            />
          );
        } else if ([holiday, leave].includes(attendanceTypeId)) {
          // 休み
          items.push(
            <ShiftCalenderItemUnassigned
              attendanceTypeId={attendanceTypeId}
              dateString={dateString}
              isApplied={
                TypeUtils.isNonNilNumber(workApplyId) &&
                TypeUtils.isNonNilString(workApply?.applyTime)
              }
              isOutsourcer={!!state.userInfo?.roleOptions?.isOutsourcer}
              onClickMatterExpense={
                state.userInfo?.roleOptions.isOutsourcer
                  ? undefined
                  : () => onClickMatterExpense(undefined, dateString)
              }
            />
          );
        } else if (TypeUtils.isNil(summary) || summary.length === 0) {
          // 予定はあるのにサマリデータがない
          // 休み
          items.push(
            <ShiftCalenderItemUnassigned
              attendanceTypeId={attendanceTypeId}
              dateString={dateString}
              isApplied={
                TypeUtils.isNonNilNumber(workApplyId) &&
                TypeUtils.isNonNilString(workApply?.applyTime)
              }
              isOutsourcer={!!state.userInfo?.roleOptions?.isOutsourcer}
              onClickMatterExpense={
                state.userInfo?.roleOptions.isOutsourcer
                  ? undefined
                  : () => onClickMatterExpense(undefined, dateString)
              }
            />
          );
        }
        //  else {
        //   //
        //   summary.forEach((item, index) =>
        //     items.push(
        //       <ShiftCalendarItemAssigned
        //         dailyMatterSummary={item}
        //         dateString={dateString}
        //         isApplied={!TypeUtils.isNilNumber(workApplyId)}
        //         isOutsourcer={state.userInfo?.role_type !== 0}
        //         onClickWorkerAssignment={
        //           !TypeUtils.isNil(master.role.representative) &&
        //           state.userInfo?.role_id.includes(
        //             master.role.representative?.id
        //           ) &&
        //           item.assignStatus === 2
        //             ? () => onClickWorkerAssign(item.dailyMatterId!)
        //             : undefined
        //         }
        //         onClickAssignment={() =>
        //           item.assignStatus !== 0
        //             ? onClickOperation(item.assignMessageId!)
        //             : undefined
        //         }
        //         onClickMatterName={() => {
        //           setSelectedMatterId(item.matterId);
        //           onOpen();
        //         }}
        //         onClickWorkReport={
        //           item.assignStatus !== 2 || !item.isLeader
        //             ? undefined
        //             : () => {
        //                 setDateState(props.dateState);
        //                 navigate(
        //                   `${pagePaths.WORK_REPORT}/${item.dailyMatterId}/${
        //                     !TypeUtils.isNilNumber(workApplyId)
        //                       ? pageKeywords.mode.VIEW
        //                       : pageKeywords.mode.EDIT
        //                   }`
        //                 );
        //               }
        //         }
        //         onClickMatterExpense={() =>
        //           onClickMatterExpense(item.dailyMatterId, dateString)
        //         }
        //         onClickWorkContent={
        //           item.assignStatus !== 2
        //             ? undefined
        //             : () => {
        //                 setDateState(props.dateState);
        //                 navigate(
        //                   `${pagePaths.WORK_CONTENT}/${dateString.replaceAll(
        //                     '/',
        //                     '-'
        //                   )}/${item.userCodeWorker!}/${
        //                     !TypeUtils.isNilNumber(workApplyId)
        //                       ? pageKeywords.mode.VIEW
        //                       : pageKeywords.mode.EDIT
        //                   }${
        //                     TypeUtils.isNilString(
        //                       state.userInfo?.outsourcer_code
        //                     ) || state.userInfo?.outsourcer_code.length === 0
        //                       ? `?index=${index}`
        //                       : `?outsourcerCode=${state.userInfo?.outsourcer_code}&index=${index}`
        //                   }`
        //                 );
        //               }
        //         }
        //         outsourcerMembers={props.outsourcerMemberSummary}
        //       />
        //     )
        //   );
        // }
      }
    );

  return (
    <>
      <StyledList hasBorder items={items || [<>予定はありません</>]} />
      <MatterDetailModal
        isOpen={isOpen}
        matterId={selectedMatterId}
        onClose={() => {
          setSelectedMatterId(undefined);
          onClose();
        }}
      />
    </>
  );
};

export default DailyMatterList;
